// SortableHeader — column header button with sort direction indicators.
// Used in list pages (risks, controls, actions, events) for client-side sorting.
//
// Sort cycling: click → desc → asc → none
//
// Usage:
//   const [sort, setSort, handleSort] = useSortState<'name' | 'status'>()
//   <SortableHeader label="Name" column="name" sort={sort} onSort={handleSort} />

import { useCallback, useState } from 'react'
import { ArrowUp, ArrowDown } from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'

// --- Sort state types ---

export type SortDirection = 'asc' | 'desc'

export interface SortState<TColumn extends string = string> {
  column: TColumn | null
  direction: SortDirection
}

// --- Hook ---

/**
 * Manages sort state with desc → asc → none cycling.
 * Returns [state, setState, handleSort] where handleSort can be passed directly
 * to SortableHeader's onSort prop.
 */
export function useSortState<TColumn extends string>() {
  const [sort, setSort] = useState<SortState<TColumn>>({
    column: null,
    direction: 'desc',
  })

  const handleSort = useCallback((column: TColumn) => {
    setSort((prev) => {
      if (prev.column === column) {
        if (prev.direction === 'desc') return { column, direction: 'asc' }
        return { column: null, direction: 'desc' }
      }
      return { column, direction: 'desc' }
    })
  }, [])

  return [sort, setSort, handleSort] as const
}

// --- Component ---

interface SortableHeaderProps<TColumn extends string> {
  /** Display label for the column. */
  label: string
  /** Column identifier passed to onSort. */
  column: TColumn
  /** Current sort state. */
  sort: SortState<TColumn>
  /** Called when the header is clicked. */
  onSort: (column: TColumn) => void
  /** Additional CSS classes for the button. */
  className?: string
  /**
   * If true, hides the label text and shows it in a tooltip instead.
   * Useful for narrow icon-only columns.
   */
  compact?: boolean
}

export function SortableHeader<TColumn extends string>({
  label,
  column,
  sort,
  onSort,
  className,
  compact,
}: SortableHeaderProps<TColumn>) {
  const active = sort.column === column

  const button = (
    <button
      type="button"
      className={cn(
        'flex items-center gap-1 select-none transition-colors hover:text-foreground',
        active && 'text-foreground',
        className,
      )}
      onClick={() => onSort(column)}
    >
      {!compact && label}
      {active ? (
        sort.direction === 'desc' ? (
          <ArrowDown className="h-3 w-3" />
        ) : (
          <ArrowUp className="h-3 w-3" />
        )
      ) : (
        <span className="h-3 w-3" />
      )}
    </button>
  )

  if (compact) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>{button}</TooltipTrigger>
        <TooltipContent>
          <p>{label}</p>
        </TooltipContent>
      </Tooltip>
    )
  }

  return button
}
